import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { API_BASE } from '../config';
import { ROLE_OPTIONS, getCurrentUser } from '../utils/roleLabel';
import { track } from '../utils/analytics';

const API = API_BASE;

// Keep client-side cap a little below the backend's 300KB ceiling so
// the leader gets a clean error here, not a 413 from the server.
const LOGO_MAX_BYTES = 280 * 1024;
const LOGO_ACCEPTED_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp', 'image/gif'];

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getCurrentUser());
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoStatus, setLogoStatus] = useState(null);
  const logoInputRef = useRef(null);
  const [roleSelection, setRoleSelection] = useState(() => {
    const u = getCurrentUser();
    const cur = u?.roleLabel || '';
    if (!cur) return '';
    return ROLE_OPTIONS.includes(cur) ? cur : 'Other';
  });
  const [customRole, setCustomRole] = useState(() => {
    const u = getCurrentUser();
    const cur = u?.roleLabel || '';
    return cur && !ROLE_OPTIONS.includes(cur) ? cur : '';
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { kind: 'success'|'error', text }

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const effectiveRoleLabel = roleSelection === 'Other'
    ? customRole.trim()
    : (roleSelection || '');

  async function patchLogo(logoData) {
    setLogoSaving(true);
    setLogoStatus(null);
    try {
      const token = localStorage.getItem('guruone_token');
      const res = await axios.patch(`${API}/auth/me`, { logoData }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = { ...user, logoData: res.data.user.logoData || null };
      setUser(next);
      localStorage.setItem('guruone_user', JSON.stringify(next));
      setLogoStatus({ kind: 'success', text: logoData ? 'Logo saved.' : 'Logo removed.' });
      track('settings_logo_saved', { hasLogo: !!logoData });
    } catch (err) {
      setLogoStatus({ kind: 'error', text: err.response?.data?.error || 'Could not save logo — please try again.' });
    }
    setLogoSaving(false);
  }

  function handleLogoFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!LOGO_ACCEPTED_MIMES.includes(file.type)) {
      setLogoStatus({ kind: 'error', text: 'Pick a PNG, JPEG, SVG, WEBP or GIF.' });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoStatus({ kind: 'error', text: `That file is too big — keep it under ${Math.round(LOGO_MAX_BYTES / 1024)}KB.` });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (!dataUrl.startsWith('data:image/')) {
        setLogoStatus({ kind: 'error', text: 'Could not read that file.' });
        return;
      }
      // Final guard — even with file.size check, base64 expansion can
      // tip the data URL above the backend's cap. Double-check here.
      if (dataUrl.length > LOGO_MAX_BYTES) {
        setLogoStatus({ kind: 'error', text: `Encoded image is too big — keep it under ${Math.round(LOGO_MAX_BYTES / 1024)}KB.` });
        return;
      }
      patchLogo(dataUrl);
    };
    reader.onerror = () => setLogoStatus({ kind: 'error', text: 'Could not read that file.' });
    reader.readAsDataURL(file);
  }

  async function saveRoleLabel() {
    setSaving(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('guruone_token');
      const res = await axios.patch(`${API}/auth/me`, { roleLabel: effectiveRoleLabel || null }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const next = { ...user, roleLabel: res.data.user.roleLabel || null };
      setUser(next);
      localStorage.setItem('guruone_user', JSON.stringify(next));
      setStatus({ kind: 'success', text: 'Saved.' });
      track('settings_role_label_saved', { value: next.roleLabel || '(default)' });
    } catch (err) {
      setStatus({ kind: 'error', text: err.response?.data?.error || 'Could not save — please try again.' });
    }
    setSaving(false);
  }

  if (!user) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9' }}>
      <Header />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.4rem' }}>Settings</h1>
        <p style={{ fontSize: '0.88rem', color: '#666', margin: '0 0 2rem' }}>
          Tell us a little about how you'd like to be addressed across the platform.
        </p>

        <section style={{
          background: 'white', borderRadius: 12, padding: '1.5rem 1.6rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '1.25rem',
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.35rem' }}>How do you describe your role?</h2>
          <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem', lineHeight: 1.5 }}>
            The platform uses this term whenever it addresses you. If you skip this, we default to "business leader".
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '0.6rem' }}>
            {ROLE_OPTIONS.map((opt) => {
              const isSel = roleSelection === opt;
              return (
                <button key={opt} type="button" onClick={() => { setRoleSelection(opt); setStatus(null); }}
                  style={{
                    padding: '0.4rem 0.9rem', fontSize: '0.8rem', borderRadius: 20,
                    border: isSel ? '1.5px solid #1D9E75' : '1.5px solid #e0e0e0',
                    background: isSel ? '#e6f5ec' : 'white',
                    color: isSel ? '#0F6E56' : '#444',
                    fontWeight: isSel ? 600 : 500,
                    cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                  }}>
                  {opt}
                </button>
              );
            })}
            <button type="button" onClick={() => { setRoleSelection('Other'); setStatus(null); }}
              style={{
                padding: '0.4rem 0.9rem', fontSize: '0.8rem', borderRadius: 20,
                border: roleSelection === 'Other' ? '1.5px solid #1D9E75' : '1.5px solid #e0e0e0',
                background: roleSelection === 'Other' ? '#e6f5ec' : 'white',
                color: roleSelection === 'Other' ? '#0F6E56' : '#444',
                fontWeight: roleSelection === 'Other' ? 600 : 500,
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              Other
            </button>
          </div>
          {roleSelection === 'Other' && (
            <input
              type="text"
              value={customRole}
              onChange={(e) => { setCustomRole(e.target.value); setStatus(null); }}
              placeholder="e.g. Chief of Staff, Partner, Co-Founder"
              maxLength={60}
              style={{
                width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.88rem',
                border: '1.5px solid #e0e0e0', borderRadius: 8, fontFamily: 'inherit',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginTop: '1.1rem' }}>
            <button
              type="button"
              onClick={saveRoleLabel}
              disabled={saving || (roleSelection === 'Other' && !customRole.trim())}
              style={{
                padding: '0.6rem 1.2rem', borderRadius: 8, background: '#1D9E75',
                color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600,
                cursor: (saving || (roleSelection === 'Other' && !customRole.trim())) ? 'not-allowed' : 'pointer',
                opacity: (saving || (roleSelection === 'Other' && !customRole.trim())) ? 0.6 : 1,
                fontFamily: 'inherit', width: 'auto',
              }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            {status && (
              <span style={{
                fontSize: '0.78rem',
                color: status.kind === 'success' ? '#0F6E56' : '#991B1B',
                fontWeight: 500,
              }}>
                {status.kind === 'success' ? '✓ ' : '✕ '}{status.text}
              </span>
            )}
          </div>
        </section>

        <section style={{
          background: 'white', borderRadius: 12, padding: '1.5rem 1.6rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '1.25rem',
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 0.35rem' }}>Brand logo</h2>
          <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem', lineHeight: 1.5 }}>
            Upload your logo and it'll appear on the one-page direction statement and other outputs. PNG, JPEG, SVG, WEBP or GIF — up to {Math.round(LOGO_MAX_BYTES / 1024)}KB.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Preview */}
            <div style={{
              width: 96, height: 96, borderRadius: 10,
              border: '1.5px dashed #d1d5db', background: '#fafafa',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', flexShrink: 0,
            }}>
              {user.logoData ? (
                <img src={user.logoData} alt="Your logo"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: '0.72rem', color: '#aaa', fontStyle: 'italic' }}>No logo</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap' }}>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={LOGO_ACCEPTED_MIMES.join(',')}
                  onChange={handleLogoFile}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current && logoInputRef.current.click()}
                  disabled={logoSaving}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: 8, background: '#1D9E75',
                    color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600,
                    cursor: logoSaving ? 'not-allowed' : 'pointer',
                    opacity: logoSaving ? 0.6 : 1,
                    fontFamily: 'inherit', width: 'auto',
                  }}>
                  {logoSaving ? 'Uploading…' : (user.logoData ? 'Replace logo' : 'Upload logo')}
                </button>
                {user.logoData && (
                  <button
                    type="button"
                    onClick={() => patchLogo(null)}
                    disabled={logoSaving}
                    style={{
                      padding: '0.5rem 0.9rem', borderRadius: 8, background: 'white',
                      color: '#991B1B', border: '1.5px solid #FECACA',
                      fontSize: '0.78rem', fontWeight: 500,
                      cursor: logoSaving ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', width: 'auto',
                    }}>
                    Remove
                  </button>
                )}
              </div>
              {logoStatus && (
                <span style={{
                  fontSize: '0.78rem',
                  color: logoStatus.kind === 'success' ? '#0F6E56' : '#991B1B',
                  fontWeight: 500,
                }}>
                  {logoStatus.kind === 'success' ? '✓ ' : '✕ '}{logoStatus.text}
                </span>
              )}
            </div>
          </div>
        </section>

        <section style={{
          background: 'white', borderRadius: 12, padding: '1.25rem 1.6rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          fontSize: '0.82rem', color: '#666', lineHeight: 1.55,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, color: '#1a1a2e', marginBottom: 2 }}>Account</div>
              <div>{user.email}</div>
              {user.businessName && <div style={{ color: '#888', marginTop: 2 }}>{user.businessName}</div>}
            </div>
            <button
              type="button"
              onClick={() => navigate('/two-factor-setup')}
              style={{
                background: 'white', border: '1.5px solid #e0e0e0', color: '#444',
                padding: '0.4rem 0.85rem', borderRadius: 8, fontSize: '0.78rem',
                fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              Two-factor settings →
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
